<template>
  <div class="qr-scanner" :class="{'ready': ready}">
    <video v-if="isBrowserApp" ref="video"></video>
    <div ref="overlay" class="qr-scanner__overlay">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 238 238">
        <path fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="M31.3 2H10a8 8 0 0 0-8 8v21.3M206.8 2H228a8 8 0 0 1 8 8v21.3m0 175.4V228a8 8 0 0 1-8 8h-21.3m-175.4 0H10a8 8 0 0 1-8-8v-21.3"/>
      </svg>
    </div>
  </div>
</template>

<script>
  import QrScanner from 'qr-scanner'

  import { BarcodeScanner, SupportedFormat } from '@capacitor-community/barcode-scanner';

  export function hasCamera() {
    return QrScanner.hasCamera()
  }

  export default {

    props: {
      isBrowserApp: { type: Boolean, required: true },
    },

    data() {
      return {
        ready: false,
        _scanner: null,
        isCordovaApp: !this.isBrowserApp, // TODO:: Better check? For now, assume Cordova if not browser app
      }
    },

    mounted() {
      this.repositionOverlay = this.repositionOverlay.bind(this)
      this._onWebScanResult = this._onWebScanResult.bind(this)

      if(this.isBrowserApp) {
        const $video = this.$refs.video
        this._scanner = new QrScanner($video, this._onWebScanResult)
        $video.addEventListener('canplay', () => this.ready = true)
      }
      else if(this.isCordovaApp) {
        this._scanner = BarcodeScanner
        this.ready = true
      }
      
      window.addEventListener('resize', this.repositionOverlay)

      this.start()
      this.repositionOverlay()
    },

    methods: {

      start() {
        if(this.isBrowserApp) {
          this._scanner.start()
        }
        else if(this.isCordovaApp) {

          // check or request permission
          BarcodeScanner.checkPermission({ force: true })
            .then(status => {
                if (status.granted) {
                  // Hide DOM elements to reveal camera stream under webview
                  document.body.querySelectorAll('[data-app] main, [data-app] header').forEach(el => el.style.display = 'none')
        
                  this._scanner.hideBackground() // make background of WebView transparent
        
                  this._scanner.startScan({ targetedFormats: [SupportedFormat.QR_CODE] })
                    .then(result => {
                      if(result.content) this.$emit('result', result.content)
                    })
                    .catch(error => this.$emit('error', error))
                }
            })
        }
      },
      
      stop() {
        if(this.isBrowserApp) {
          this._scanner.stop()
        }
        else if(this.isCordovaApp) {
          this._scanner.showBackground()
          this._scanner.stopScan()
          document.body.querySelectorAll('[data-app] main, [data-app] header').forEach(el => el.style.display = null)
        }
      },
      
      _onWebScanResult(result) {
        this.$emit('result', result)
      },

      repositionOverlay() {
        requestAnimationFrame(() => {
          const scannerHeight = this.$el.offsetHeight
          const scannerWidth = this.$el.offsetWidth
          const smallerDimension = Math.min(scannerHeight, scannerWidth)

          if (smallerDimension === 0) return // component not visible or destroyed
          const overlaySize = Math.ceil(2 / 3 * smallerDimension)
          // not always the accurate size of the sourceRect for QR detection in QrScanner (e.g. if video is
          // landscape and screen portrait) but looks nicer in the UI.
          const $qrOverlay = this.$refs.overlay
          $qrOverlay.style.width = overlaySize + 'px'
          $qrOverlay.style.height = overlaySize + 'px'
          $qrOverlay.style.top = ((scannerHeight - overlaySize) / 2) + 'px'
          $qrOverlay.style.left = ((scannerWidth - overlaySize) / 2) + 'px'
        })
      }
    },

    destroyed() {
      if(this._scanner) {
        this.stop()
        this._scanner.destroy && this._scanner.destroy()
      }
      window.removeEventListener('resize', this.repositionOverlay)
    }
  }
</script>


<style scoped>
  .qr-scanner {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
  video {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    object-fit: cover;
    display: block;
    opacity: 0;
    transition: opacity .3s;
  }
  .qr-scanner__overlay {
    position: absolute;
    opacity: 0;
    animation: qr-scanner__overlay-animation 400ms infinite alternate ease-in-out;

  }
  
  .qr-scanner.ready video,
  .qr-scanner.ready .qr-scanner__overlay {
    opacity: 1;
  }

  @keyframes qr-scanner__overlay-animation {
    from {
      transform: scale(.99);
    }
    to {
      transform: scale(1.01);
    }
  }
  .qr-scanner__overlay svg {
    width: 100%;
    height: 100%;
    stroke: blue;
  }
</style>